import type { HttpClient } from '@/lib/http/HttpClient';
import type { LabelingExperiment, LabelingExperimentLabels } from '../types/LabelingTypes';
import type { SessionLabelData, ToolLabelData } from '../utils/labelExport';
import { labelizeListExperimentLabels } from './LabelingApiClient';

/** One request per experiment, each fanning out server-side; a small pool keeps Redis responsive. */
const MAX_PARALLEL_EXPERIMENTS = 4;

export interface CollectLabelExportOptions {
  signal?: AbortSignal;
  /** Called after each experiment has been fully fetched. */
  onProgress?: (done: number, total: number) => void;
}

/** Re-attaches each annotator's labels to the experiment's images, which the route lists once. */
function toSessions({
  experimentId,
  sessions,
  images,
}: LabelingExperimentLabels): SessionLabelData[] {
  if (!Array.isArray(sessions) || !Array.isArray(images)) {
    throw new Error(`Unexpected labels payload for experiment ${experimentId}`);
  }
  return sessions.map(({ labels, ...session }) => {
    const labelByKey = new Map(labels.map(({ imageKey, label }) => [imageKey, label]));
    return {
      session,
      images: images.map((image) => ({ ...image, label: labelByKey.get(image.imageKey) ?? null })),
    };
  });
}

/**
 * Fetches, for every experiment, the labels of **every annotator** of the caller's company.
 * Every experiment gets an entry — with no session when nobody labeled it — and results keep the
 * order of `experiments` whatever order the requests settle in.
 */
export async function collectLabelExportData(
  http: HttpClient,
  experiments: LabelingExperiment[],
  { signal, onProgress }: CollectLabelExportOptions = {}
): Promise<ToolLabelData[]> {
  const results: SessionLabelData[][] = experiments.map(() => []);
  let nextIndex = 0;
  let done = 0;

  const worker = async () => {
    while (nextIndex < experiments.length) {
      const index = nextIndex;
      nextIndex += 1;
      const { experimentId } = experiments[index];
      results[index] = toSessions(await labelizeListExperimentLabels(http, experimentId, signal));
      done += 1;
      onProgress?.(done, experiments.length);
    }
  };

  const poolSize = Math.min(MAX_PARALLEL_EXPERIMENTS, experiments.length);
  await Promise.all(Array.from({ length: poolSize }, worker));
  return experiments.map(({ experimentId }, index) => ({
    experimentId,
    sessions: results[index],
  }));
}
